import { ScrollTrigger, initSmoothScroll } from '@/modules/smoothScroll'
import { initLoading } from '@/modules/loading'
import { initMenu } from '@/modules/menu'
import { initParallax } from '@/modules/parallax'
import { initMainVisual } from '@/modules/mainVisual'
import { initTitleReveal } from '@/modules/titleReveal'
import { initScrubText } from '@/modules/scrubText'
import { initRoots } from '@/modules/roots'
import { initSideScroll } from '@/modules/sideScroll'
import { initCarousels } from '@/modules/carousel'
import { initShare } from '@/modules/share'

/**
 * エントリポイント。
 *
 * スクロール演出（GSAP / ScrollTrigger）は要素の実寸を前提にするため、
 * ローディング解除＝画像読み込み完了を待ってから組み立てる。
 * DOM 依存のない UI（メニュー・シェア）は先に有効化しておく。
 */
function boot(): void {
  initSmoothScroll()
  initMenu()
  initShare()
  initCarousels()

  // スクロール演出の組み立てとFVの初期状態の適用は、ローディングの
  // オーバーレイがまだ不透明なうち（prepare）に済ませる。
  // フェード後にやると、生のページが見えたあと要素が消えて再登場する
  // 二重描画になってしまう（特に回線の遅いモバイルで目立つ）。
  let playMainVisual: (() => void) | undefined
  void initLoading(() => {
    playMainVisual = initMainVisual()
    initParallax()
    initTitleReveal()
    initScrubText()
    initRoots()
    initSideScroll()

    // ピン留めと横スクロールの区間長は実寸依存なので、最後にまとめて再計算する
    ScrollTrigger.refresh()
  }).then(() => {
    // オーバーレイが消えてから FV の登場演出を再生する
    playMainVisual?.()
  })

  // resize は ScrollTrigger 自身が見ているので、ここで重ねて refresh() しない。
  //
  // 以前はここで window の resize を拾って毎回 refresh() していたが、
  // iOS Safari はスクロール中にアドレスバーが伸縮するたび resize を投げる。
  // refresh() は全トリガーの開始・終了位置を測り直す＝13,000px の文書全体に
  // 同期レイアウトを強いる処理なので、スクロールのたびに引っかかっていた。
  // ScrollTrigger.config の ignoreMobileResize（smoothScroll.ts）で、
  // 高さだけが変わる種類の resize は無視させている。
}

/* boot（GSAP / ScrollTrigger の組み立て）は合計 1 秒級のメインスレッド
   仕事で、DCL 直後に始めると最初のフレームすら出せず FCP がまるごと
   遅れる。先にブラウザへ 1 フレーム描かせてから始める（rAF 2 回 =
   「次のフレームの描画が終わった直後」）。その間もローディング幕は
   CSS アニメーションが自走しているので見た目には空白が出ない */
function bootAfterFirstPaint(): void {
  requestAnimationFrame(() => requestAnimationFrame(boot))
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootAfterFirstPaint, { once: true })
} else {
  bootAfterFirstPaint()
}
